import moment from 'moment';
import { BaseWebComponentTranslator } from 'apps/webcomponents/src/framework/BaseWebComponentTranslator';
import { BreachPathDetailsResourceSideMenuDataType } from '../types';
import { html, TemplateResult } from 'lit';
import { DEVICES_CLASS, AffectedDeviceType, OS_TYPE, OS_CLASS, CloudProviders } from 'apps/webcomponents/src/riskManagement/common/constants';
import { FontSizes, FontWeights } from 'apps/webcomponents/src/types/common/types';
import { BreachPathNodeDefaultMetadata, BreachPathNodeRisk } from '../../breachPathDetails/types';
import { getBulletColorForScore } from 'apps/webcomponents/src/riskManagement/common/utils';
import { getTextForScore } from 'apps/webcomponents/src/riskManagement/common/customRenderers';

export function headerIconRenderer(data: BreachPathDetailsResourceSideMenuDataType): TemplateResult {
  let borderColor = 'transparent';
  
  if (data.type === AffectedDeviceType.EC2_INSTANCE) {
    borderColor = 'var(--red-alert-500-severe)';
  } else if (data.type === AffectedDeviceType.EC2_NETWORK_ACL || data.type === AffectedDeviceType.NETWORK_NETWORK_SECURITY_GROUPS) {
    borderColor = 'var(--orange-alert-chart-100)';
  } else if (data.type === AffectedDeviceType.VPCINTERNET_GATEWAY) {
    borderColor = 'var(--yellow-alert-050)';
  }

  return html`
    <div class="node-wrapper" style="border-color: ${borderColor};">
      <gz-breach-path-graph-icon
        name="${DEVICES_CLASS.get(data.type)}"
        color="var(--white-layout-300-primary)"
        size="24"
      ></gz-breach-path-graph-icon>
    </div>
  `;
}

export function resourceNameRenderer(data: BreachPathDetailsResourceSideMenuDataType): string {
  return data.name || '-';
}

export function resourceTypeTextRenderer(data: BreachPathDetailsResourceSideMenuDataType): string {
  return BaseWebComponentTranslator.getInstance().getDeviceTypes(data.type) || '-';
}

export function resourceTypeRenderer(data: BreachPathDetailsResourceSideMenuDataType): TemplateResult {
  return html`
    <div class="content-with-icon">
      <gz-breach-path-graph-icon
        name="${DEVICES_CLASS.get(data.type)}"
        color="var(--chart-302-icon-buttons-titles)"
      ></gz-breach-path-graph-icon>
      <gz-tooltip
        isEllipsis
        tooltipText="${BaseWebComponentTranslator.getInstance().getDeviceTypes(data.type)}"
      >
        <gz-text
          class="d-inline"
          fontSize="${FontSizes.PARAGRAPH_12}"
          fontWeight="${FontWeights.NORMAL}"
          style="color: var(--main-titles-inputs-600);"
        >
          ${BaseWebComponentTranslator.getInstance().getDeviceTypes(data.type)}
        </gz-text>
      </gz-tooltip>
    </div>
  `;
}

export function riskScoreRenderer(data: BreachPathDetailsResourceSideMenuDataType): TemplateResult | string {
  try {
    const metadata: BreachPathNodeRisk = JSON.parse(data.risk);
    
    if (!metadata?.information?.score_breakdown) {
      return '-';
    }

    return html`
      <gz-bullet color="${getBulletColorForScore(metadata.information.score_breakdown.finalScore)}">
        <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" style="color: var(--main-titles-inputs-600);">
          ${getTextForScore(metadata.information.score_breakdown.finalScore)} (${metadata.information.score_breakdown.finalScore}%)
        </gz-text>
      </gz-bullet>
    `;
  } catch {
    return '-';
  }
}

export function resourceIdRenderer(data: BreachPathDetailsResourceSideMenuDataType): string {
  try {
    const metadata: BreachPathNodeDefaultMetadata = JSON.parse(data.metadata);
    return metadata.resource_id;
  } catch {
    return '-';
  }
}

export function platformRenderer(data: BreachPathDetailsResourceSideMenuDataType): TemplateResult | string {
  try {
    const metadata: BreachPathNodeDefaultMetadata = JSON.parse(data.metadata);

    let provider;

    if (metadata.provider === 'aws') {
      provider = CloudProviders.AWS;
    } else if (metadata.provider === 'gcp') {
      provider = CloudProviders.GCP;
    } else if (metadata.provider === 'azr') {
      provider = CloudProviders.AZURE;
    }

    if (!provider) {
      return '-';
    }
  
    return html`
      <div class="content-with-icon">
        <gz-breach-path-graph-icon
          name="ds-${OS_CLASS.get(provider)}"
          color="var(--chart-302-icon-buttons-titles)"
        ></gz-breach-path-graph-icon>
        <gz-tooltip
          isEllipsis
          tooltipText="${BaseWebComponentTranslator.getInstance().getOperatingSystem(provider)}"
        >
          <gz-text
            class="d-inline"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            style="color: var(--main-titles-inputs-600);"
          >
            ${BaseWebComponentTranslator.getInstance().getOperatingSystem(provider)}
          </gz-text>
        </gz-tooltip>
      </div>
    `;
  } catch {
    return '-';
  }
}

export function lastScannedOnRenderer(data: BreachPathDetailsResourceSideMenuDataType): string {
  const date = data.updatedAt;
  return date ? moment(date).format('DD MMM YYYY, HH:mm') : '-';
}

export function accountIdRenderer(data: BreachPathDetailsResourceSideMenuDataType): string {
  try {
    const metadata: BreachPathNodeDefaultMetadata = JSON.parse(data.metadata);
    return metadata.cloud_account;
  } catch {
    return '-';
  }
}

export function regionRenderer(data: BreachPathDetailsResourceSideMenuDataType): string {
  try {
    const metadata: BreachPathNodeDefaultMetadata = JSON.parse(data.metadata);
    return metadata.region ?? '-';
  } catch {
    return '-';
  }
}

export function networkInterfaceIdRenderer(data: BreachPathDetailsResourceSideMenuDataType): string {
  return '-';
}

export function subnetIdRenderer(data: BreachPathDetailsResourceSideMenuDataType): string {
  return '-';
}

export function virtualPrivateCloudIdRenderer(data: BreachPathDetailsResourceSideMenuDataType): string {
  return '-';
}
